import { flexRender, HeaderContext, Renderable } from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { type MainTableProps } from '../types';
import { cn } from '@/lib/utils';
import { ObisCodeMapType } from '@/store/hes/types/other';
import CopyToClipBoardBtn from './CopyToClipBoardBtn';
import React, { useRef, useState, useEffect } from 'react';

const getObisCode = ({
  obisCodeMap,
  paramName
}: {
  obisCodeMap?: ObisCodeMapType;
  paramName: string;
}): string => {
  return (
    obisCodeMap?.find((item) => {
      const formattedParamName = item.paramName.replaceAll('_', ' ');

      return formattedParamName === paramName;
    })?.obisCode || ''
  );
};

function MainTable<T>({
  table,
  columns,
  obisCodeMap,
  modifiedColumnNames,
  expansionDepth,
  needsVirtualization,
  expandableComponent,
  onRowClicked,
  clipBoardCols
}: MainTableProps<T>) {
  const onMouseEnter = (cellId: string) => {
    document.getElementById(`copy-btn-${cellId}`)?.classList.remove('hidden');
  };
  const onMouseLeave = (cellId: string) => {
    document.getElementById(`copy-btn-${cellId}`)?.classList.add('hidden');
  };
  const getModifiedColumnName = (
    header: Renderable<HeaderContext<T, unknown>>
  ): React.ReactNode => {
    const columns = table.getAllColumns();
    const correspondingColumn = columns.find((e) => {
      return e.columnDef.header === header;
    });
    const modifiedName = modifiedColumnNames?.find((e) => {
      return e.field === correspondingColumn?.id; // here id is the original column name recieved in the API response
    });
    return modifiedName?.label || header?.toString();
  };

  const rows = table.getRowModel().rows;
  const rowHeight = 52; // px, matches TableCell height
  const buffer = 5;
  const visibleRowCount = 10;
  const containerStyle: React.CSSProperties = needsVirtualization
    ? { height: rowHeight * visibleRowCount, overflowY: 'auto' }
    : { height: 'auto', overflowY: 'unset' };

  const [scrollTop, setScrollTop] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const totalRows = rows.length;
  const startIdx = Math.max(0, Math.floor(scrollTop / rowHeight) - buffer);
  const endIdx = Math.min(
    totalRows - 1,
    startIdx + visibleRowCount + buffer * 2
  );

  useEffect(() => {
    // Reset scroll when data changes
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [rows.length]);

  return (
    <div
      ref={scrollRef}
      className="main-table-scroll-container"
      style={containerStyle}
      onScroll={
        needsVirtualization
          ? (e) => setScrollTop(e.currentTarget.scrollTop)
          : undefined
      }>
      {rows.length === 0 ? (
        <div className="flex items-center bg-white justify-center p-5 h-full w-full text-center text-gray-500">
          No results.
        </div>
      ) : (
        <Table>
          <TableHeader
            className={
              expansionDepth === 1
                ? 'depth-1-table-header'
                : expansionDepth === 2
                ? 'depth-2-table-header'
                : 'table-header'
            }>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const paramName = header.column.columnDef.header as string;
                  const obisCode = getObisCode({ obisCodeMap, paramName });
                  return (
                    <TableHead
                      key={header.id}
                      className="text-nowrap font-bold text-[#0A3690] text-xs h-[40px]">
                      {header.isPlaceholder ? null : (
                        <>
                          <div
                            className={
                              header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                            title={
                              header.column.getCanSort()
                                ? header.column.getNextSortingOrder() === 'asc'
                                  ? 'Sort ascending'
                                  : header.column.getNextSortingOrder() ===
                                    'desc'
                                  ? 'Sort descending'
                                  : 'Clear sort'
                                : undefined
                            }>
                            {flexRender(
                              modifiedColumnNames
                                ? getModifiedColumnName(
                                    header.column.columnDef.header
                                  )
                                : header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' 🔼',
                              desc: ' 🔽'
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                          {obisCode && (
                            <div className="text-[#0A3690] text-[14px] font-bold font-normal">
                              {`[${obisCode}]`}
                            </div>
                          )}
                        </>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="bg-white">
            {needsVirtualization
              ? rows.map((row, idx) => {
                  if (idx < startIdx || idx > endIdx) {
                    // ghost row
                    return (
                      <TableRow
                        key={row.id}
                        style={{ height: rowHeight }}>
                        <TableCell colSpan={columns.length} />
                      </TableRow>
                    );
                  }
                  // real row
                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        onClick={() => onRowClicked?.(row.original)}
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                        className={cn(
                          'hover:bg-[none]',
                          idx % 2 !== 0 && 'bg-[#F3F9F9]',
                          onRowClicked && 'cursor-pointer',
                          expansionDepth === 2 &&
                            idx % 2 !== 0 &&
                            'bg-[#DAFFFF99]'
                        )}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <TableCell
                              onMouseEnter={() => onMouseEnter(cell.id)}
                              onMouseLeave={() => onMouseLeave(cell.id)}
                              id={cell.id}
                              key={cell.id}
                              className="h-[52px] text-xs lg:text-sm text-nowrap text-[#464E5F]">
                              {
                                // @ts-expect-error: Accessor key used to check if cell is an action or generic column
                                (!cell.column.columnDef.accessorKey?.includes(
                                  'action_key'
                                ) &&
                                  cell.getValue() === undefined) ||
                                cell.getValue() === null ? (
                                  '--'
                                ) : (
                                  <div className="flex items-center  gap-2">
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                    {clipBoardCols?.includes(
                                      cell.column.columnDef.header as string
                                    ) && (
                                      <CopyToClipBoardBtn
                                        id={`copy-btn-${cell.id}`}
                                        className="hidden"
                                        val={cell.getValue() as string}
                                      />
                                    )}
                                  </div>
                                )
                              }
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      {row.getIsExpanded() && expandableComponent && (
                        <TableRow>
                          <td colSpan={row.getAllCells().length}>
                            {React.createElement(expandableComponent, {
                              row: row.original
                            })}
                          </td>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              : rows.map((row, idx) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      onClick={() => onRowClicked?.(row.original)}
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className={cn(
                        'hover:bg-[none]',
                        idx % 2 !== 0 && 'bg-[#F3F9F9]',
                        onRowClicked && 'cursor-pointer',
                        expansionDepth === 2 &&
                          idx % 2 !== 0 &&
                          'bg-[#DAFFFF99]'
                      )}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          onMouseEnter={() => onMouseEnter(cell.id)}
                          onMouseLeave={() => onMouseLeave(cell.id)}
                          id={cell.id}
                          key={cell.id}
                          className="h-[52px] text-xs lg:text-sm text-nowrap text-[#464E5F]">
                          {
                            // @ts-expect-error: Accessor key used to check if cell is an action or generic column
                            (!cell.column.columnDef.accessorKey?.includes(
                              'action_key'
                            ) &&
                              cell.getValue() === undefined) ||
                            cell.getValue() === null ? (
                              '--'
                            ) : (
                              <div className="flex items-center  gap-2">
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                                {clipBoardCols?.includes(
                                  cell.column.columnDef.header as string
                                ) && (
                                  <CopyToClipBoardBtn
                                    id={`copy-btn-${cell.id}`}
                                    className="hidden"
                                    val={cell.getValue() as string}
                                  />
                                )}
                              </div>
                            )
                          }
                        </TableCell>
                      ))}
                    </TableRow>
                    {row.getIsExpanded() && expandableComponent && (
                      <TableRow>
                        <td colSpan={row.getAllCells().length}>
                          {React.createElement(expandableComponent, {
                            row: row.original
                          })}
                        </td>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default MainTable;
